import React, { Dispatch, SetStateAction, useState } from "react";
import useMobile from "../../hooks/useMobile";
import { dynamicFailed, dynamicSuccess } from "../../hooks/useToast";
import { INotice } from "../../exports/interfaces";
import { fetchNotices } from "../../api/NoticeApi";
import { useLoader } from "../../context/LoaderContext";
import axiosClient from "../../axioxClient.ts/axiosClient";
import Select from "react-select";
import { customSelectStyles } from "../../exports/customSelectStyles";
import { Upload } from "lucide-react";
import { useOption } from "../../context/OptionContext";

const NoticeDrawer = ({
  openDrawer,
  setOpenDrawer,
  setNotice,
}: {
  openDrawer: boolean;
  setOpenDrawer: Dispatch<SetStateAction<boolean>>;
  setNotice: Dispatch<SetStateAction<INotice[]>>;
}) => {
  const { setLoading } = useLoader();
  const { categories } = useOption();
  const isMobile = useMobile();
  const [fileName, setFileName] = useState("");
  const noticeTypeOptions =
    categories
      ?.find((item) => item.name === "Notice Type")
      ?.dropdowns?.filter((item) => item.status === "Active")
      ?.sort((a, b) => a.order - b.order)
      ?.map((item) => ({
        value: item.label,
        label: item.label,
      })) || [];

  const [formData, setFormData] = useState({
    noticeType: "",
    noticeTitle: "",
    noticeDetails: "",
    publishDate: "",
    endDate: "",
    documentUrl: null as File | null,
  });

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData((prev) => ({
        ...prev,
        documentUrl: file,
      }));
      setFileName(file.name);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const payload = new FormData();

      payload.append("noticeType", formData.noticeType);
      payload.append("noticeTitle", formData.noticeTitle);
      payload.append("noticeDetails", formData.noticeDetails);
      payload.append("publishDate", formData.publishDate);
      payload.append("endDate", formData.endDate);

      if (formData.documentUrl) {
        payload.append("documentUrl", formData.documentUrl);
      }

      const response = await axiosClient.post("/notices", payload, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      if (response.status === 200 || response.status === 201) {
        fetchNotices()
          .then((noticeData) => {
            setNotice(noticeData);
            setTimeout(() => {
              setLoading(false);
              dynamicSuccess("Notice sent successfully!");
            }, 1000);
          })
          .catch((error) => {
            setTimeout(() => {
              dynamicFailed(`${error.message}`);
              setLoading(false);
            }, 1000);
          });
      }
      handleClose();
    } catch (error: any) {
      setTimeout(() => {
        dynamicFailed(`${error.message}`);
        setLoading(false);
      }, 1000);
    }
  };

  const handleClose = () => {
    setOpenDrawer(false);
    setFormData({
      noticeType: "",
      noticeTitle: "",
      noticeDetails: "",
      publishDate: "",
      endDate: "",
      documentUrl: null,
    });
    setFileName("");
  };
  return (
    <>
      <div
        className={`notice-drawer ${openDrawer ? "open" : ""}  d-flex flex-column overflowx-auto`}
      >
        <button
          className="btn p-0 text-end position-absolute top-0 end-0 m-3"
          onClick={handleClose}
        >
          <i className="fa-solid fa-x"></i>
        </button>
        <div className="p-3">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h3>Create New Notice</h3>
          </div>

          {/* Form Card */}
          <div className="overflow-x-auto inner-container p-1 custom-scrollbar">
            <div className="row g-3">
              {/* Notice Type */}
              <div className="col-md-8">
                <label className="form-label">
                  Notice Type <span className="primary-color">*</span>
                </label>
                <Select
                  styles={customSelectStyles}
                  options={noticeTypeOptions}
                  placeholder="Select Notice Category"
                  value={
                    noticeTypeOptions.find(
                      (option) => option.value === formData.noticeType,
                    ) || null
                  }
                  onChange={(selected) =>
                    setFormData((prev) => ({
                      ...prev,
                      noticeType: selected?.value || "",
                    }))
                  }
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>

              {!isMobile && <div className="col-md-4"></div>}

              {/* Title */}
              <div className="col-md-12">
                <label className="form-label">
                  {" "}
                  Notice Title <span className="primary-color">*</span>
                </label>
                <input
                  className="form-control"
                  type="text"
                  name="noticeTitle"
                  value={formData.noticeTitle}
                  onChange={handleChange}
                  placeholder="Enter Notice Title here"
                />
              </div>

              {/* Details */}
              <div className="col-12">
                <label className="form-label">Notice Details</label>
                <textarea
                  className="form-control"
                  rows={5}
                  name="noticeDetails"
                  value={formData.noticeDetails}
                  onChange={handleChange}
                  placeholder="Enter Notice Details here"
                />
              </div>

              {/* File Upload */}
              <div className="col-12">
                <label className="form-label">
                  Upload Document (PDF, JPEG, PNG)
                </label>

                <div className="upload-box text-center p-4">
                  <div className="mb-2">
                    <i>
                      <Upload />
                    </i>
                  </div>

                  <p className="mb-1">
                    Choose a file or drag and drop it here.
                  </p>

                  <small className="d-block mb-3">
                    (txt, docx, xlsx, pdf, jpeg, png - Up to 100 mb)
                  </small>

                  {fileName && (
                    <small className="d-block mb-3">{fileName}</small>
                  )}

                  <label className="btn btn-badge text-main-color px-4 mb-0">
                    {fileName ? "Change file" : "Browse File"}
                    <input type="file" hidden onChange={handleFileChange} />
                  </label>
                </div>
              </div>

              {/* Dates */}
              <div className="col-md-6">
                <label className="form-label">
                  Notice Publish Date <span className="primary-color">*</span>
                </label>
                <input
                  type="date"
                  className="form-control"
                  name="publishDate"
                  value={formData.publishDate}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-6">
                <label className="form-label">
                  Notice End Date <span className="primary-color">*</span>
                </label>
                <input
                  type="date"
                  className="form-control"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* Submit */}
            <div className="text-end mt-4">
              <button
                className="btn btn-badge-primary px-3"
                onClick={handleSubmit}
              >
                Send Notice
              </button>
            </div>
            {/* Back */}
            <div className="text-end mt-3">
              <button
                className="btn btn-badge-primary px-3"
                onClick={handleClose}
              >
                Back
              </button>
            </div>
          </div>
        </div>
      </div>
      {openDrawer && (
        <div className="notice-overlay" onClick={handleClose}></div>
      )}
    </>
  );
};

export default NoticeDrawer;
